// Challenge Funnel page-content schema: default sections + editable field definitions.
const T = (t, extra = {}) => ({ t, ...extra });
const clone = (o) => JSON.parse(JSON.stringify(o));

const CTA_DARK = { label: 'Save my free seat', bg: '#141414', fg: '#F0EFEC' };
const CTA_LIGHT = { label: 'Save my free seat', bg: '#F0EFEC', fg: '#141414' };

/* ── default sections per page ─────────────────────────────────────────── */
const LANDER = [
  { id: 'countdown', type: 'countdown', props: {
    label: T('Free live event — {{date}} @ {{time}}'), bg: '#141414', fg: '#F0EFEC', show_timer: true } },
  { id: 'hero', type: 'hero', props: {
    eyebrow: T('For AI operators & agencies running GoHighLevel'),
    headline: T('The 1-Day *AI Agency* Challenge'),
    subhead: T('Get the exact AI agents, scripts & systems agencies are using to earn **$20K–$100K/mo** — and walk away with your first agent built.'),
    show_video: true, video_url: '',
    cta: clone(CTA_DARK),
    micro: T('Free · One live session · Seats capped for live Q&A'),
    show_cards: true } },
  { id: 'stack', type: 'stack', props: {
    headline: T('Get The System.\nSkip The Headaches. *Launch In Days.*'),
    body: T("Your seat includes the live event and free access to **Voxinity AI** — the platform you'll use to build the system live. No code, no complicated setup — just a **client-ready AI agent**."),
    cta: clone(CTA_DARK),
    date_line: T('{{date}} @ {{time}} · Free · Online'),
    show_icons: true } },
  { id: 'conversation', type: 'conversation', props: {
    eyebrow: T('This is the product'),
    headline: T('Local businesses pay *$2K–$5K+ upfront* for this — then a monthly retainer on top.'),
    para1: T('An AI agent that answers every call and message, qualifies the lead, handles objections, and books the appointment — synced straight to the CRM.'),
    para2: T("Build it once. Get paid the setup fee **and** recurring support income every single month. At the Challenge, you'll watch one get built live — **and leave with it.**"),
    bg: '#141414' } },
  { id: 'bullets', type: 'bullets', props: {
    headline: T("In one live session, *you'll discover:*"),
    items: [
      { title: 'The 15-Minute Agent Build', desc: 'watch us create a fully working AI agent LIVE, in front of you' },
      { title: 'The "One-Call Close"', desc: 'the method behind agencies scaling to $20K–$100K/month without cold calling or paid ads' },
      { title: 'The Pricing Trap', desc: 'why 90% of AI agencies charge wrong and lose clients in month one — and the model that locks clients in for years' },
      { title: 'The 7 Money Niches', desc: 'the local business types practically begging for this right now' },
      { title: 'The First Client Blueprint', desc: 'leave with a step-by-step plan to sign your next paying client within 30 days' },
    ],
    cta: clone(CTA_DARK) } },
  { id: 'fit', type: 'fit', props: {
    headline: T('For People Who Want *Paying AI Clients* — Not Another Course'),
    subhead: T("If you want to build AI agents that local businesses pay **$2K–$5K+ upfront** for — plus a monthly retainer — this Challenge was built for you. If you're chasing a magic passive-income button, this isn't the right fit."),
    for_title: T('This is for you if...'),
    for_items: [
      { text: 'You want to **start an AI agency** — or add AI agents to your existing agency, GHL, or service business' },
      { text: "You're ready to **show up live and build alongside us** — no tech background needed, you leave with a working agent" },
      { text: 'You want **$2K–$5K+ upfront per client** plus a monthly retainer — real recurring income, not one-off gigs' },
    ],
    notfor_title: T('This is not for you if...'),
    notfor_items: [
      { text: "You're hunting for **passive income with zero effort** — this is a real business, not a lottery ticket" },
      { text: 'You **collect courses and never take action** — the Challenge is built for doers, not note-takers' },
      { text: "You're **not willing to talk to local business owners** — that's exactly where the money comes from" },
    ],
    cta: clone(CTA_DARK),
    date_line: T('{{date}} @ {{time}} · Free · Online') } },
  { id: 'authority', type: 'authority', props: {
    eyebrow: T("This isn't theory"),
    headline: T('A real playbook, from a *real business.*'),
    body: T("We run **Skye AI** — an AI consulting business serving hundreds of local businesses. We've helped home service companies book **50 extra appointments in 30 days** and agency operators scale to **$20K–$100K/month.** Now we're opening the playbook.") } },
  { id: 'reviews', type: 'reviews', props: {
    rows_top: [
      { name: 'Sarah McLeod', role: 'Agency Owner · Voxinity Partner', text: 'Hey man, just wanted to touch base and update you! Closed that guy! Wooohoo…have our onboarding call on Thursday! He loves the sound of your agents too! I did the orb and he was very impressed! 🙏🙏🙏' },
      { name: 'Marcus Reid', role: 'Agency Owner', text: 'Signed two clients my first week using the scripts. The one-call close is real — $4K upfront plus a monthly retainer on each.' },
      { name: 'Danielle Ortiz', role: 'GHL Agency Owner', text: "White-labeled everything. My clients think I built this in-house — that's the whole point. Best margin service I've ever sold." },
      { name: 'Jake Thornton', role: 'AI Operator', text: 'Went from selling websites to selling agents. First deal: $3,500 setup + recurring support. Never going back.' },
      { name: 'Priya Shah', role: 'Marketing Consultant', text: 'The niche templates paid for themselves 100x over. Booked a dental office demo the same afternoon I got access.' },
    ],
    rows_bottom: [
      { name: 'Mike Delgado', role: 'HVAC Company Owner', text: 'It answered 63 calls last month while my techs were on jobs. We booked 22 of them. This thing prints money.' },
      { name: 'Dr. Alyssa Chen', role: 'Dental Practice', text: 'No-shows are down 40%. The AI confirms every appointment by text without my front desk lifting a finger.' },
      { name: 'Tony Marchetti', role: 'Restaurant Owner', text: 'It takes reservations at 11pm. I finally sleep. My hostess thinks we hired someone new.' },
      { name: 'Kayla Brooks', role: 'Med Spa Owner', text: 'Every Instagram DM gets answered in seconds now. Consults doubled in the first month.' },
      { name: 'Rob Feldman', role: 'Real Estate Broker', text: 'Missed calls used to cost me listings. Not anymore — every single lead gets a callback in under a minute.' },
    ] } },
  { id: 'final_cta', type: 'final_cta', props: {
    headline: T('One day. One agent. *One plan.*'),
    body: T("{{date}} @ {{time}} — when seats are gone, they're gone."),
    bg: '#141414',
    cta: clone(CTA_LIGHT) } },
];

const KIT = [
  { id: 'kit_header', type: 'step_header', props: { step_label: T('Step 2 of 3 — Your seat is saved'), dots: 2 } },
  { id: 'kit_intro', type: 'kit_intro', props: {
    headline: T('Your seat is saved. Do you want to show up *prepared?*'),
    subhead: T('Attendees who start with the **AI Agency Launch Kit** hit Day 1 with the scripts, templates, and systems already installed — so the event becomes execution, not just education.') } },
  { id: 'kit_offer', type: 'kit_offer', props: {
    stack_title: T('The AI Agency Launch Kit'),
    items: [
      { item: 'Training: How Agencies Add $20K–$100K/Mo Selling AI Agents', value: '$497' },
      { item: 'Proven Client-Closing Scripts — word-for-word, the exact ones Skye AI uses', value: '$297' },
      { item: 'Battle-Tested Plug-and-Play Agent Snapshot Template', value: '$497' },
      { item: 'AI-Agent Prompt Template — the prompt engineering done for you', value: '$197' },
      { item: 'The Onboarding Engine — turn a signed client into a launched agent without chaos', value: '$297' },
      { item: 'BONUS: 30-Day Voxinity Trial — build your first agent free', value: '$897/mo' },
    ],
    total_label: T('Total value'), total_value: T('$2,682+'),
    price_label: T('Today only — challenge registrants'), price_display: T('$27'),
    bump_title: T('Add the 5-Agent Niche Vault — $47'),
    bump_desc: T('Pre-built agent templates for HVAC, dental, real estate, med spa & restaurants. Sell-ready on day one. **(Value: $985)**'),
    checkout_label: T('Secure checkout'),
    guarantee_text: T('14-day money-back guarantee'),
    encrypt_note: T('Your payment details are encrypted and processed by Stripe. We never see or store your card.'),
    review_text: T("\"Closed my first $3.5K client twelve days after the challenge — the kit's scripts did the heavy lifting. Easily paid for itself 100x.\""),
    review_author: T('— Marcus T., agency owner') } },
  { id: 'kit_decline', type: 'kit_decline', props: {
    decline_text: T("No thanks — I'll show up empty-handed and figure it out myself."),
    urgency_text: T("This offer appears once — it's not available after this page.") } },
];

export const THREE_JOBS_DEFAULTS = {
  s1_label: T('Step 1 — Lock it in'), s1_title: T('Add the Challenge to your calendar NOW'),
  s2_label: T("Step 2 — Don't go alone"), s2_title: T('Join the private Challenge Community'),
  s2_body: T('Introductions are happening now. Members are 3x more likely to show up — and win.'),
  s2_cta: T('Join the Community →'),
  s3_label: T('Step 3 — Your one job'),
  s3_body: T("Before the Challenge, do ONE thing: **write down 3 local businesses you personally know.** Bring that list. You'll see why on event day."),
  video_url: '',
};

const THANKS = [
  { id: 'thanks_header', type: 'step_header', props: { step_label: T("Step 3 of 3 — You're registered · {{date}} @ {{time}}"), dots: 3 } },
  { id: 'thanks_hero', type: 'thanks_hero', props: {
    headline: T("You're in! *Three quick things.*"),
    subhead: T('Do these now — they take 2 minutes and triple your odds of actually winning from this.') } },
  { id: 'three_jobs', type: 'three_jobs', props: clone(THREE_JOBS_DEFAULTS) },
];

const VIP = [
  { id: 'vip_header', type: 'step_header', props: { step_label: T("Step 3 of 3 — Kit unlocked, you're officially in"), dots: 3 } },
  { id: 'vip_hero', type: 'vip_hero', props: {
    headline: T("Payment confirmed. *Here's exactly what to do next.*"),
    subhead: T('Left: lock in the event. Right: activate your Voxinity account — your 30-day trial is part of the kit.') } },
  { id: 'vip_main', type: 'vip_main', props: {
    banner_text: T('Included in your $27 kit: **30 days of Voxinity, free.**'),
    card_title: T('Activate your Voxinity account'),
    card_body: T("The Launch Kit is the playbook. **Voxinity is the machine that runs it** — the same platform we'll use LIVE at the Challenge to build your first agent. Activate now so you're ready on event day."),
    plan_label: T('Pick your plan — free for 30 days'),
    includes_text: T('**Every plan includes the core platform** — AI voice + chat agents, client re-billing, AI Wizard, Instant Inbox & the AI Workforce. Voice starting at $0.12/min; AI model usage billed separately. AI chat $0.025/msg. **Starter unlocks BYOK; Agency & Pro add full white-label** and more client capacity.'),
    why_card: T("**Why the card?** $0 today — nothing is charged for 30 days, and you can cancel in one click from your dashboard. We ask now so your agent doesn't shut off mid-client when your trial converts."),
    cta_label: T('Start my free 30 days →'),
    trust_line: T('$0 due today · 30 days free · Cancel in one click · No contract'),
    subnote: T("You'll use this exact account live at the Challenge.") } },
];

const PAGE_SETTINGS = () => ({ id: '_page', type: 'page_settings', props: {
  bg: '',
  reel: { enabled: false, video_url: '', overlay: 'Click here to unmute', cta: clone(CTA_DARK) },
  proof: { enabled: false, every_s: 12, ghosts: false, ghost_every_s: 25 },
} });
const withPage = (sections) => [PAGE_SETTINGS(), ...sections];

export const DEFAULT_PAGES = { lander: withPage(LANDER), kit: withPage(KIT), thanks: withPage(THANKS), vip: withPage(VIP) };

/* ── generic addable blocks ────────────────────────────────────────────── */
export const DEFAULT_BLOCK_PROPS = {
  text_section: { eyebrow: T(''), headline: T('New section headline'), body: T('Write your copy here. Use **bold** or *accent* to style words.'), show_cta: false, cta: clone(CTA_DARK) },
  cta_band: { headline: T('Ready to build your first agent?'), body: T("{{date}} @ {{time}} — when seats are gone, they're gone."), bg: '#141414', cta: clone(CTA_LIGHT) },
  video_section: { headline: T('Watch this first'), video_url: '' },
  table: {
    headline: T('Free vs *VIP* — choose your access'),
    cols: [
      { header: 'Feature', sub: '', width: 'wide', highlight: false },
      { header: 'Free Pass', sub: 'Attend live', width: '', highlight: false },
      { header: 'VIP Pass', sub: 'Everything, forever', width: '', highlight: true },
    ],
    rows: [
      { cells: [{ kind: 'text', t: 'Attend the full live Challenge' }, { kind: 'check' }, { kind: 'check' }] },
      { cells: [{ kind: 'text', t: 'Build your AI agent live' }, { kind: 'check' }, { kind: 'check' }] },
      { cells: [{ kind: 'text', t: 'Replay access' }, { kind: 'text', t: '5 days' }, { kind: 'text', t: 'Lifetime' }] },
      { cells: [{ kind: 'text', t: 'VIP-only Q&A sessions' }, { kind: 'x' }, { kind: 'check' }] },
      { cells: [{ kind: 'text', t: '' }, { kind: 'button', v: { label: 'Claim Free Seat', bg: '#2563EB', fg: '#FFFFFF', fx: 'none' } }, { kind: 'button', v: { label: 'Upgrade to VIP', bg: '#F5C518', fg: '#141414', fx: 'none' } }] },
    ],
    bg: '',
  },
  plans: {
    headline: T('Choose your *Challenge* experience'),
    subhead: T('Build your AI agent live either way. Choose how much access, coaching and support you want.'),
    cards: [
      { badge: '', title: 'Free Pass', price: '$0', price_note: 'Complete Challenge access', chip: '',
        features: ['Complete 1-Day LIVE Challenge', 'Build your AI agent live', 'Private community access', '5-day replay access'],
        cta: { label: 'Save my free seat now!', bg: '#FFFFFF', fg: '#141414', fx: 'none' }, foot: 'No payment required',
        bg: '#DBEAFE', fg: '#141414', highlight: false },
      { badge: 'Recommended', title: 'VIP AI Agents Kit', price: '$47', price_note: 'One-time', chip: 'Less than $16/day',
        features: ['VIP-only Q&A + coaching sessions', 'Lifetime access to every recording', 'Complete implementation package', 'Done-for-you AI agent templates', 'Quick-start prompt cheat sheet'],
        cta: { label: 'Get the VIP experience — $47', bg: '#F5C518', fg: '#141414', fx: 'none' }, foot: 'One payment. Lifetime access.',
        bg: '#1A1A4E', fg: '#FFFFFF', highlight: true },
    ],
    bg: '#141414',
  },
  image_marquee: { headline: T(''), images_top: [], images_bottom: [], size: 'md', bg: '' },
  spacer: { size: 'md' },
};
export const BLOCK_LIBRARY = ['text_section', 'cta_band', 'video_section', 'table', 'plans', 'image_marquee', 'spacer'];
export const newBlock = (type) => ({
  id: `blk_${Math.random().toString(36).slice(2, 9)}`,
  type,
  enabled: true,
  props: clone(DEFAULT_BLOCK_PROPS[type] || {}),
});

/* ── extra elements (added text / button / video inside a section) ─────── */
export const newExtra = (type) => ({
  id: `x_${Math.random().toString(36).slice(2, 9)}`,
  type,
  at: 'end',
  v: type === 'button'
    ? { label: 'Click here →', bg: '#141414', fg: '#F0EFEC', fx: 'nudge' }
    : type === 'video'
      ? ''
      : { t: 'New text — click it in the preview to edit' },
});

// Which fields inside each section can anchor an added element ('end' is always available)
export const EXTRA_ANCHORS = {
  hero: ['eyebrow', 'headline', 'subhead', 'video_url', 'cta', 'micro'],
  stack: ['headline', 'body', 'cta', 'date_line'],
  conversation: ['eyebrow', 'headline', 'para1', 'para2'],
  bullets: ['headline', 'items', 'cta'],
  fit: ['headline', 'subhead', 'cta', 'date_line'],
  authority: ['eyebrow', 'headline', 'body'],
  final_cta: ['headline', 'body', 'cta'],
  text_section: ['eyebrow', 'headline', 'body', 'cta'],
  cta_band: ['headline', 'body', 'cta'],
  video_section: ['headline', 'video_url'],
  table: ['headline'],
  plans: ['headline', 'subhead'],
  image_marquee: ['headline'],
  kit_intro: ['headline', 'subhead'],
  thanks_hero: ['headline', 'subhead'],
  vip_hero: ['headline', 'subhead'],
};

/* ── section metadata (names + locks for the editor) ───────────────────── */
export const SECTION_META = {
  page_settings: { name: 'Page design', pinned: true },
  countdown: { name: 'Countdown banner' },
  hero: { name: 'Hero + VSL' },
  stack: { name: 'The Stack' },
  conversation: { name: 'Live conversation' },
  bullets: { name: "What you'll discover" },
  fit: { name: 'For / Not for' },
  authority: { name: 'Authority' },
  reviews: { name: 'Reviews marquee' },
  final_cta: { name: 'Final CTA band' },
  step_header: { name: 'Page header' },
  kit_intro: { name: 'Intro headline' },
  kit_offer: { name: 'Offer + checkout', locked: true, note: 'The checkout is functional — the $27 / $47 amounts charged are fixed by the payment system. Texts here are display copy.' },
  kit_decline: { name: 'Decline link' },
  thanks_hero: { name: 'Headline' },
  three_jobs: { name: 'The 3 steps' },
  vip_hero: { name: 'Headline' },
  vip_main: { name: 'Steps + activation', locked: true, note: 'The 3 steps on the left are edited on the Thank You page. The activation form is functional — plan prices come from billing.' },
  text_section: { name: 'Text section' },
  cta_band: { name: 'CTA band' },
  video_section: { name: 'Video' },
  table: { name: 'Comparison table' },
  plans: { name: 'Side-by-side plans' },
  image_marquee: { name: 'Image slider' },
  spacer: { name: 'Spacer' },
};

/* ── editable field definitions per block type ──────────────────────────── */
export const FIELD_DEFS = {
  page_settings: [
    { k: 'bg', label: 'Page background', kind: 'color', def: '' },
  ],
  countdown: [
    { k: 'label', label: 'Banner text', kind: 'text' },
    { k: 'bg', label: 'Banner background', kind: 'color', def: '#141414' },
    { k: 'fg', label: 'Banner text color', kind: 'color', def: '#F0EFEC' },
    { k: 'show_timer', label: 'Show countdown timer', kind: 'toggle' },
  ],
  hero: [
    { k: 'eyebrow', label: 'Eyebrow', kind: 'text' },
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'show_video', label: 'Show VSL video', kind: 'toggle' },
    { k: 'video_url', label: 'VSL video (Wistia — paste anything)', kind: 'video' },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
    { k: 'micro', label: 'Micro-trust line', kind: 'text' },
    { k: 'show_cards', label: 'Floating product cards', kind: 'toggle' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  stack: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'body', label: 'Body copy', kind: 'text', widthable: true },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
    { k: 'date_line', label: 'Date line', kind: 'text' },
    { k: 'show_icons', label: 'Show fanned app icons', kind: 'toggle' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  conversation: [
    { k: 'eyebrow', label: 'Eyebrow', kind: 'text' },
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'para1', label: 'Paragraph 1', kind: 'text', widthable: true },
    { k: 'para2', label: 'Paragraph 2', kind: 'text', widthable: true },
    { k: 'bg', label: 'Section background', kind: 'color', def: '#141414' },
  ],
  bullets: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'items', label: 'Bullets', kind: 'list', itemFields: [
      { k: 'title', label: 'Title', kind: 'input' },
      { k: 'desc', label: 'Description', kind: 'textarea' },
    ] },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  fit: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'for_title', label: '"For you" card title', kind: 'text' },
    { k: 'for_items', label: '"For you" items', kind: 'list', itemFields: [{ k: 'text', label: 'Item', kind: 'textarea' }] },
    { k: 'notfor_title', label: '"Not for you" card title', kind: 'text' },
    { k: 'notfor_items', label: '"Not for you" items', kind: 'list', itemFields: [{ k: 'text', label: 'Item', kind: 'textarea' }] },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
    { k: 'date_line', label: 'Date line', kind: 'text' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  authority: [
    { k: 'eyebrow', label: 'Eyebrow', kind: 'text' },
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'body', label: 'Body copy', kind: 'text', widthable: true },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  reviews: [
    { k: 'rows_top', label: 'Top row reviews', kind: 'list', itemFields: [
      { k: 'name', label: 'Name', kind: 'input' },
      { k: 'role', label: 'Role', kind: 'input' },
      { k: 'text', label: 'Review', kind: 'textarea' },
    ] },
    { k: 'rows_bottom', label: 'Bottom row reviews', kind: 'list', itemFields: [
      { k: 'name', label: 'Name', kind: 'input' },
      { k: 'role', label: 'Role', kind: 'input' },
      { k: 'text', label: 'Review', kind: 'textarea' },
    ] },
  ],
  final_cta: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'body', label: 'Body line', kind: 'text' },
    { k: 'bg', label: 'Band background', kind: 'color', def: '#141414' },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
  ],
  step_header: [
    { k: 'step_label', label: 'Step label', kind: 'text' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  kit_intro: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  kit_offer: [
    { k: 'stack_title', label: 'Offer card title', kind: 'text' },
    { k: 'items', label: 'Offer stack items', kind: 'list', itemFields: [
      { k: 'item', label: 'Item', kind: 'textarea' },
      { k: 'value', label: 'Value (e.g. $497)', kind: 'input' },
    ] },
    { k: 'total_label', label: 'Total label', kind: 'text' },
    { k: 'total_value', label: 'Total value', kind: 'text' },
    { k: 'price_label', label: 'Price banner label', kind: 'text' },
    { k: 'price_display', label: 'Displayed price', kind: 'text' },
    { k: 'bump_title', label: 'Order bump title', kind: 'text' },
    { k: 'bump_desc', label: 'Order bump description', kind: 'text' },
    { k: 'checkout_label', label: 'Checkout panel label', kind: 'text' },
    { k: 'guarantee_text', label: 'Guarantee text', kind: 'text' },
    { k: 'encrypt_note', label: 'Encryption note', kind: 'text' },
    { k: 'review_text', label: 'Review quote', kind: 'text' },
    { k: 'review_author', label: 'Review author', kind: 'text' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  kit_decline: [
    { k: 'decline_text', label: 'Decline link text', kind: 'text' },
    { k: 'urgency_text', label: 'Urgency line', kind: 'text' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  thanks_hero: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  three_jobs: [
    { k: 's1_label', label: 'Step 1 label', kind: 'text' },
    { k: 's1_title', label: 'Step 1 title', kind: 'text' },
    { k: 's2_label', label: 'Step 2 label', kind: 'text' },
    { k: 's2_title', label: 'Step 2 title', kind: 'text' },
    { k: 's2_body', label: 'Step 2 body', kind: 'text' },
    { k: 's2_cta', label: 'Step 2 button text', kind: 'text' },
    { k: 's3_label', label: 'Step 3 label', kind: 'text' },
    { k: 's3_body', label: 'Step 3 body', kind: 'text' },
    { k: 'video_url', label: 'Homework video (Wistia — paste anything)', kind: 'video' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  vip_hero: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  vip_main: [
    { k: 'banner_text', label: 'Included banner', kind: 'text' },
    { k: 'card_title', label: 'Card title', kind: 'text' },
    { k: 'card_body', label: 'Card body', kind: 'text' },
    { k: 'plan_label', label: 'Plan picker label', kind: 'text' },
    { k: 'includes_text', label: '"Every plan includes" paragraph', kind: 'text' },
    { k: 'why_card', label: '"Why the card?" box', kind: 'text' },
    { k: 'cta_label', label: 'CTA button text', kind: 'text' },
    { k: 'trust_line', label: 'Trust line', kind: 'text' },
    { k: 'subnote', label: 'Sub-note', kind: 'text' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  text_section: [
    { k: 'eyebrow', label: 'Eyebrow', kind: 'text' },
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'body', label: 'Body copy', kind: 'text', widthable: true },
    { k: 'show_cta', label: 'Show CTA button', kind: 'toggle', defaultOff: true },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  cta_band: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'body', label: 'Body line', kind: 'text' },
    { k: 'bg', label: 'Band background', kind: 'color', def: '#141414' },
    { k: 'cta', label: 'CTA button', kind: 'cta' },
  ],
  video_section: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'video_url', label: 'Video (Wistia — paste anything)', kind: 'video' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  table: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'cols', label: 'Table', kind: 'table' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  plans: [
    { k: 'headline', label: 'Headline', kind: 'text' },
    { k: 'subhead', label: 'Sub-headline', kind: 'text', widthable: true },
    { k: 'cards', label: 'Plan cards', kind: 'plans' },
    { k: 'bg', label: 'Section background', kind: 'color', def: '#141414' },
  ],
  image_marquee: [
    { k: 'headline', label: 'Headline (optional)', kind: 'text' },
    { k: 'images_top', label: 'Images', kind: 'images' },
    { k: 'size', label: 'Image size', kind: 'select', options: [['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']] },
    { k: 'bg', label: 'Section background', kind: 'color', def: '' },
  ],
  spacer: [
    { k: 'size', label: 'Spacer size', kind: 'select', options: [['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']] },
    { k: 'bg', label: 'Spacer color', kind: 'color', def: '' },
  ],
};

/* ── merge saved content over defaults ──────────────────────────────────── */
export const mergePage = (page, saved) => {
  const defs = DEFAULT_PAGES[page] || [];
  if (!Array.isArray(saved) || saved.length === 0) return clone(defs);
  const res = saved.map((s) => {
    const def = defs.find((d) => d.id === s.id);
    const base = def ? def.props : DEFAULT_BLOCK_PROPS[s.type] || {};
    return { ...s, props: { ...clone(base), ...(s.props || {}) } };
  });
  if (!res.some((s) => s.type === 'page_settings')) res.unshift(PAGE_SETTINGS());
  return res;
};

export const mergeAll = (savedPages = {}) => ({
  lander: mergePage('lander', savedPages.lander),
  kit: mergePage('kit', savedPages.kit),
  thanks: mergePage('thanks', savedPages.thanks),
  vip: mergePage('vip', savedPages.vip),
});
